/**
 * Fluidité B4 (P5#17) — Garantie de parfait achèvement (GPA) dans l'onglet Réception.
 *
 * - Bandeau « Garantie de parfait achèvement jusqu'au … » dès qu'un PV est signé.
 * - Client : « Signaler un désordre » → photo, quelques mots, Envoyer (3 touchers + photo).
 *   La demande arrive « à qualifier » chez l'architecte.
 * - Maîtrise d'œuvre : nouvelle demande (lot → entreprise retrouvée), « Transmettre à
 *   l'entreprise » (e-mail même sans compte), levée en deux temps comme une réserve.
 * - Entreprise : les demandes de ses lots (réponse par « Fait, avec photo » dans l'app terrain).
 * API : backend/routes/fin_chantier_routes.py (/gpa).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, Camera, Plus, PaperPlaneTilt, CheckCircle, XCircle, Trash } from '@phosphor-icons/react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Badge } from '../../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { BOUTONS } from '../../../lib/libelles';
import { raisonNonEnvoi } from '../../../lib/envoiEmail';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const dateFr = (iso) => {
  if (!iso) return '';
  const d = new Date(String(iso).length <= 10 ? `${iso}T12:00:00` : iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString('fr-FR');
};

const COULEUR = {
  ouverte: 'bg-[#FFF8E1] text-[#7A5A00] border-[#B8860B]',
  a_verifier: 'bg-[#E3F2FD] text-[#1E3A5F] border-[#1E3A5F]',
  levee: 'bg-[#E8F5E9] text-[#2D6A4F] border-[#2D6A4F]',
};

export default function DemandesGPA({ chantierId, isMoe, isClient, onDecisionLevee, refreshKey }) {
  const [data, setData] = useState(null);
  const [signalerOpen, setSignalerOpen] = useState(false);
  const [transmettre, setTransmettre] = useState(null);

  const charger = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API_URL}/api/chantiers/${chantierId}/gpa`, { withCredentials: true });
      setData(d);
    } catch {
      setData(null);
    }
  }, [chantierId]);

  useEffect(() => { charger(); }, [charger, refreshKey]);

  if (!data || (!data.gpa?.gpa_fin && !(data.demandes || []).length)) return null;
  const { gpa, demandes = [], lots = [] } = data;

  return (
    <div className="bg-white border border-[#E0E0DB] p-4 space-y-3" data-testid="reception-gpa">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ShieldCheck size={18} className="text-[#2D6A4F]" />
          <h3 className="font-medium text-[#2C2C2C]">Garantie de parfait achèvement (GPA)</h3>
        </div>
        {data.peut_declarer && gpa?.en_cours && (
          <Button onClick={() => setSignalerOpen(true)} className="rounded-none bg-[#C4773B] hover:bg-[#A85F25] gap-2" data-testid="gpa-signaler">
            {isClient ? <><Camera size={16} /> Signaler un désordre</> : <><Plus size={16} /> Nouvelle demande en GPA</>}
          </Button>
        )}
      </div>
      {gpa?.gpa_fin && (
        <p className="text-sm text-[#2C2C2C]" data-testid="gpa-fin">
          {gpa.en_cours
            ? <>Réception du {dateFr(gpa.date_reception)} : l'entreprise doit reprendre les désordres signalés jusqu'au <strong>{dateFr(gpa.gpa_fin)}</strong> ({gpa.jours_restants} jour{gpa.jours_restants > 1 ? 's' : ''}).</>
            : <>La garantie de parfait achèvement a pris fin le {dateFr(gpa.gpa_fin)}.</>}
        </p>
      )}
      {isClient && gpa?.en_cours && (
        <p className="text-xs text-[#6B6B6B]">Un défaut apparu depuis la réception ? Prenez une photo et décrivez-le : votre architecte le transmet à l'entreprise concernée.</p>
      )}

      {demandes.length > 0 && (
        <div className="divide-y divide-[#F0F0EB] border border-[#F0F0EB]">
          {demandes.map((d) => (
            <div key={d.id} className="p-3 flex gap-3 items-start" data-testid={`gpa-demande-${d.id}`}>
              {d.photo_url
                ? <a href={d.photo_url} target="_blank" rel="noreferrer"><img src={d.photo_url} alt="" className="w-14 h-14 object-cover border border-[#E0E0DB]" /></a>
                : <div className="w-14 h-14 bg-[#F5F5F3] border border-[#E0E0DB]" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#2C2C2C]">{d.description}</p>
                <p className="text-xs text-[#6B6B6B] flex flex-wrap gap-x-3">
                  {d.lot ? <span>Lot {d.lot}{d.lot_nom ? ` — ${d.lot_nom}` : ''}{d.entreprise ? ` · ${d.entreprise}` : ''}</span> : <span>Lot à désigner</span>}
                  {d.localisation && <span>{d.localisation}</span>}
                  <span>Signalée le {dateFr(d.created_at)}{d.declaree_par ? ` par ${d.declaree_par}` : ''}</span>
                  {d.echeance && d.statut === 'ouverte' && !d.a_qualifier && <span>Intervention avant le {dateFr(d.echeance)}</span>}
                </p>
                {d.hors_delai && <p className="text-xs text-[#8B3A3A]">Signalée après la fin de la garantie.</p>}
                {isMoe && (d.envois || []).length > 0 && (
                  <p className="text-xs text-[#6B6B6B]">
                    E-mail à l'entreprise : {d.envois.map((e) => (e.statut === 'envoye' ? `envoyé${e.email ? ` (${e.email})` : ''}` : `non envoyé (${raisonNonEnvoi(e.statut)})`)).join(', ')}
                  </p>
                )}
                {d.statut === 'a_verifier' && d.derniere_preuve && (
                  <p className="text-xs text-[#1E3A5F] mt-1">
                    Preuve de {d.derniere_preuve.by_user_label || "l'entreprise"}{d.derniere_preuve.commentaire ? ` : ${d.derniere_preuve.commentaire}` : ''}
                  </p>
                )}
              </div>
              <div className="flex flex-col items-end gap-1">
                <Badge className={`rounded-none border ${d.a_qualifier ? 'bg-[#F5F5F3] text-[#4A4A4A] border-[#9A9A9A]' : COULEUR[d.statut] || ''}`}>
                  {d.a_qualifier ? (isMoe ? 'À qualifier' : 'Reçue par l\'architecte') : d.statut_libelle}
                </Badge>
                {isMoe && d.a_qualifier && (
                  <Button size="sm" className="h-7 rounded-none bg-[#1E3A5F] hover:bg-[#2C4F7C] gap-1 text-xs" onClick={() => setTransmettre(d)} data-testid={`gpa-transmettre-${d.id}`}>
                    <PaperPlaneTilt size={12} /> Transmettre à l'entreprise
                  </Button>
                )}
                {isMoe && d.statut === 'a_verifier' && (
                  <div className="flex gap-1">
                    <Button size="sm" className="h-7 rounded-none bg-[#2D6A4F] hover:bg-[#1B4332] gap-1 text-xs" onClick={() => onDecisionLevee?.(d, true, charger)}>
                      <CheckCircle size={12} /> {BOUTONS.validerLevee}
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 rounded-none gap-1 text-xs text-[#8B3A3A]" onClick={() => onDecisionLevee?.(d, false, charger)}>
                      <XCircle size={12} /> {BOUTONS.refuserLevee}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {signalerOpen && (
        <SignalerDialog chantierId={chantierId} isClient={isClient} lots={lots} onClose={() => setSignalerOpen(false)}
          onCreated={() => { setSignalerOpen(false); charger(); }} />
      )}
      {transmettre && (
        <TransmettreDialog chantierId={chantierId} demande={transmettre} lots={lots} onClose={() => setTransmettre(null)}
          onDone={() => { setTransmettre(null); charger(); }} />
      )}
    </div>
  );
}

function SignalerDialog({ chantierId, isClient, lots, onClose, onCreated }) {
  const [description, setDescription] = useState('');
  const [lot, setLot] = useState('');
  const [localisation, setLocalisation] = useState('');
  const [photo, setPhoto] = useState(null);
  const [apercu, setApercu] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [horsDelai, setHorsDelai] = useState(null);
  const fileRef = useRef(null);

  const choisirPhoto = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error('Photo trop volumineuse (10 Mo au plus)'); return; }
    setPhoto(f);
    try { setApercu(URL.createObjectURL(f)); } catch { setApercu(null); }
  };

  const envoyer = async (confirme = false) => {
    if (description.trim().length < 3) { toast.warning('Décrivez le désordre en quelques mots'); return; }
    setEnvoi(true);
    try {
      let photoUrl = null;
      if (photo) {
        const fd = new FormData();
        fd.append('file', photo, photo.name || `gpa_${Date.now()}.jpg`);
        fd.append('doc_type', 'photo');
        fd.append('force_duplicate', 'true');
        fd.append('visible_to', JSON.stringify(['archi', 'moe_principal', 'moe_membre', 'consultant', 'entreprise', 'client']));
        fd.append('notes', 'Désordre signalé en garantie de parfait achèvement');
        const up = await axios.post(`${API_URL}/api/chantiers/${chantierId}/upload`, fd, { withCredentials: true });
        photoUrl = up?.data?.url || up?.data?.media_url || null;
      }
      await axios.post(`${API_URL}/api/chantiers/${chantierId}/gpa`, {
        description: description.trim(), lot: lot || null, localisation: localisation.trim() || null,
        photo_url: photoUrl, hors_delai_confirme: confirme,
      }, { withCredentials: true });
      toast.success(isClient ? 'Merci : votre architecte est prévenu' : 'Demande en GPA enregistrée');
      onCreated();
    } catch (e) {
      const msg = e.response?.data?.detail || "La demande n'a pas pu être envoyée";
      if (!isClient && e.response?.status === 409 && /Confirmez/.test(msg)) setHorsDelai(msg);
      else toast.error(msg);
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-lg" data-testid="gpa-dialog">
        <DialogHeader><DialogTitle>{isClient ? 'Signaler un désordre' : 'Nouvelle demande en GPA'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={choisirPhoto} className="hidden" />
          {apercu ? (
            <div className="relative">
              <img src={apercu} alt="" className="w-full max-h-48 object-cover" />
              <button type="button" onClick={() => { setPhoto(null); setApercu(null); }} className="absolute top-1 right-1 bg-black/60 text-white p-2" aria-label="Retirer la photo"><Trash size={14} /></button>
            </div>
          ) : (
            <button type="button" onClick={() => fileRef.current?.click()} className="w-full border border-dashed border-[#9A9A9A] bg-[#FAFAF8] py-4 text-sm flex items-center justify-center gap-2" data-testid="gpa-photo">
              <Camera size={20} /> Prendre une photo
            </button>
          )}
          <div>
            <Label>Qu'avez-vous constaté ?</Label>
            <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} className="rounded-none"
              placeholder="Ex. : infiltration sous la baie vitrée du séjour" data-testid="gpa-description" />
          </div>
          {!isClient && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Lot</Label>
                <select value={lot} onChange={(e) => setLot(e.target.value)} className="w-full border border-[#E0E0DB] p-2 text-sm bg-white" data-testid="gpa-lot">
                  <option value="">À désigner</option>
                  {lots.map((l) => <option key={l.code} value={l.code}>{l.code} — {l.nom}{l.entreprise ? ` · ${l.entreprise}` : ''}</option>)}
                </select>
              </div>
              <div>
                <Label>Localisation</Label>
                <Input value={localisation} onChange={(e) => setLocalisation(e.target.value)} className="rounded-none" placeholder="Pièce, niveau…" />
              </div>
            </div>
          )}
          {horsDelai && (
            <div className="border border-[#8B3A3A] bg-[#FFF5F5] p-2 text-xs text-[#8B3A3A]">
              {horsDelai}
              <div className="mt-2">
                <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" disabled={envoi} onClick={() => envoyer(true)} data-testid="gpa-hors-delai-confirmer">
                  Enregistrer quand même (hors délai)
                </Button>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-none" onClick={onClose}>Annuler</Button>
            <Button className="rounded-none bg-[#C4773B] hover:bg-[#A85F25]" disabled={envoi || description.trim().length < 3} onClick={() => envoyer(false)} data-testid="gpa-envoyer">
              {envoi ? 'Envoi…' : 'Envoyer'}
            </Button>
          </div>
          {!isClient && <p className="text-xs text-[#6B6B6B]">Avec un lot, l'entreprise est prévenue tout de suite (par e-mail si elle n'a pas de compte).</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TransmettreDialog({ chantierId, demande, lots, onClose, onDone }) {
  const [lot, setLot] = useState(demande.lot || lots[0]?.code || '');
  const [echeance, setEcheance] = useState(demande.echeance || '');
  const [envoi, setEnvoi] = useState(false);
  const choisi = lots.find((l) => l.code === lot);

  const valider = async () => {
    setEnvoi(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/chantiers/${chantierId}/gpa/${demande.id}/transmettre`,
        { lot, echeance: echeance || null }, { withCredentials: true });
      // Sprint 1 — « envoyée » seulement si l'e-mail est parti ; sinon la raison.
      if (data.email_status === 'envoye') toast.success(data.message);
      else if (data.entreprise_prevenue) toast.warning(data.message);
      else toast.error(`${data.message} Prévenez l'entreprise par téléphone.`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Transmission impossible');
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-md" data-testid="gpa-transmettre-dialog">
        <DialogHeader><DialogTitle>Transmettre à l'entreprise</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-[#2C2C2C]">{demande.description}</p>
          <div>
            <Label>Lot concerné</Label>
            <select value={lot} onChange={(e) => setLot(e.target.value)} className="w-full border border-[#E0E0DB] p-2 text-sm bg-white">
              {lots.map((l) => <option key={l.code} value={l.code}>{l.code} — {l.nom}</option>)}
            </select>
            {choisi && <p className="text-xs text-[#6B6B6B] mt-1">Entreprise : {choisi.entreprise || '—'} · garantie jusqu'au {dateFr(choisi.gpa_fin)}</p>}
          </div>
          <div>
            <Label>Intervention souhaitée avant le</Label>
            <Input type="date" value={echeance} onChange={(e) => setEcheance(e.target.value)} className="rounded-none" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-none" onClick={onClose}>Annuler</Button>
            <Button className="rounded-none bg-[#1E3A5F] hover:bg-[#2C4F7C]" disabled={envoi || !lot} onClick={valider}>Transmettre</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
